/**
 * Who pays for ingesting media into a given library.
 *
 * The backend charges ingest and storage to the `billingId` carried on the library object,
 * not to whoever performs the upload. When a user adds media to a library owned by another
 * billing account, that account is billed — so the credit/storage figures we compute from
 * `billingStore` (always the *current user's* balance) describe the wrong wallet, and the
 * gates built on them block an upload the payer can well afford.
 *
 * **Not the same question as `resolveLibraryOwnership`, and the difference is load-bearing.**
 * That one answers "whose library is this?" from `owner.userId`, which is what the "Owned by
 * X" lines on the Libraries page render. This one answers "whose wallet?" from `billingId`.
 * They diverge on an org or SSO account, where several users share one billing account: a
 * teammate's library is owned by somebody else and paid for out of the *same* wallet the
 * uploader's own balance is measured against. Deciding the billing question from ownership
 * therefore skips both pre-flight balance gates on a library the user really does pay for,
 * tells them "their credits, not yours" about their own money, and — worst — withholds the
 * top-up CTA after a 402 while insisting a top-up wouldn't help. Only `billingId` may relax a
 * gate; only `owner` may name a person. Read them together where a surface needs both (see
 * `LibraryStore.isBilledToAnotherAccount` and `LibraryStore.otherOwnerName`).
 *
 * Deliberately three-valued. `unknown` is not a synonym for `self`: it means one of the two
 * ids never arrived (a library fetched before `/user/me` resolved, a store holding partial
 * data, a field the backend omitted), and the only safe reading of that is "don't claim
 * anything". Callers must map `unknown` onto today's behaviour — never onto the warning.
 */
export type BillingOwnership = 'self' | 'other' | 'unknown';

/**
 * Compare a library's billing account against the current user's.
 *
 * Both ids must be present and non-empty to return a verdict; anything else is `unknown`.
 * Note the asymmetry that follows from that: a missing id can never produce `other`, so an
 * absent field costs us a warning we should have shown, never a warning we shouldn't have.
 * That's the correct trade — the false positive tells the user someone else pays when they
 * in fact do, which is both alarming and wrong.
 */
export function resolveBillingOwnership(
  libraryBillingId: string | null | undefined,
  userBillingId: string | null | undefined,
): BillingOwnership {
  if (!libraryBillingId || !userBillingId) return 'unknown';
  return libraryBillingId === userBillingId ? 'self' : 'other';
}

/**
 * The single predicate every consumer should branch on: true only when we positively know
 * another account foots the bill. Keeps `unknown` from ever reaching a warning string or
 * relaxing a gate — see the type docstring.
 */
export function isBilledToAnotherAccount(ownership: BillingOwnership): boolean {
  return ownership === 'other';
}

/**
 * The current user's own billing id, from the two places it can come from.
 *
 * `GET /user/me` is allowed to omit `billingId` — the field exists in the response schema but
 * is not in its `required` set — and without a second source the whole cross-billing feature
 * silently no-ops for every user it's missing on (every library resolves to `unknown`). The
 * accounts most likely to omit it are the org/SSO ones that upload into other people's
 * libraries, i.e. precisely the cohort this is for, so the no-op would be near-total *and*
 * invisible. `GET /billing/info` describes this same user's billing account and its credit
 * notes carry a required `billingId`, so they answer the same question.
 *
 * The fallback only speaks when every id it's given agrees. A history spanning two billing
 * accounts would otherwise let a stale one decide the comparison, and a wrong `'other'` verdict
 * tells someone their upload is on a stranger's tab when it isn't — the exact false positive
 * `resolveBillingOwnership` is built to avoid. Disagreement, or nothing to go on, returns
 * undefined, which resolves to `unknown` and keeps every surface silent.
 */
export function resolveUserBillingId(
  userBillingId: string | null | undefined,
  creditNoteBillingIds: readonly (string | null | undefined)[],
): string | undefined {
  if (userBillingId) return userBillingId;

  const ids = new Set(creditNoteBillingIds.filter((id): id is string => !!id));
  return ids.size === 1 ? [...ids][0] : undefined;
}
